/**
 * Workshop modal content — agenda, venue/map, what-to-bring checklist,
 * and pre-workshop questionnaire data keyed by workshop id.
 *
 * Ported from:
 *   - `_legacy/xaes-content.js` lines 202-281 — WS4 agenda / bring /
 *     questionnaire structures.
 *   - `_legacy/ws4-session.js` lines 10-35 — WS4 venue + date/time
 *     fields (vanilla shipped these as '—' placeholders; we substitute
 *     plausible defaults so the modal renders meaningfully in demo).
 *   - `_legacy/wd.html` lines 137-441 — modal markup that established
 *     the field shape (venue, address, date/time, transit, parking,
 *     access; agenda sessions; bring categories + items + note;
 *     questionnaire intro + questions).
 *
 * Workshops other than WS4 get generic placeholder content of the
 * same shape — they have no vanilla source data.
 */

import type { Workshop } from "@/content/workshops";

type WorkshopId = Workshop["id"];

/* ----------------------------- Agenda ----------------------------- */

export type AgendaItemType = "session" | "break";

export type AgendaItem = {
  /** 24h start time, e.g. "09:00". */
  time: string;
  title: string;
  /** Short description, e.g. "30 min · All attendees". */
  detail: string;
  /** Duration in minutes — drives the timeline pill. */
  durationMin: number;
  type: AgendaItemType;
};

export type AgendaContent = {
  date: string;
  venue: string;
  timeRange: string;
  sessions: AgendaItem[];
};

/* ------------------------------ Map ------------------------------- */

export type MapContent = {
  venueName: string;
  address: string;
  date: string;
  time: string;
  parking: string;
  refreshments: string;
};

/* ------------------------------ Bring ----------------------------- */

export type BringCategoryTag = "REQUIRED" | "OPTIONAL";

export type BringItem = {
  label: string;
  description: string;
};

export type BringCategory = {
  label: string;
  tag: BringCategoryTag;
  items: BringItem[];
};

export type BringContent = {
  categories: BringCategory[];
  note: string;
};

/* -------------------------- Questionnaire ------------------------- */

export type QuestionnaireQuestion = {
  id: string;
  label: string;
  /**
   * "input" = single-line text, "textarea" = multi-line,
   * "choice" = single-select from `options`, "scale" = integer rating
   * (`min`–`max`, defaulting 1–5). Typed answers (choice/scale) are what
   * the facilitator results view can chart; free text is grouped by AI.
   */
  type: "input" | "textarea" | "choice" | "scale";
  placeholder?: string;
  /** Choice questions only — the selectable options, in display order. */
  options?: string[];
  /** Scale questions only — inclusive bounds; default 1 and 5. */
  min?: number;
  max?: number;
};

export type QuestionnaireContent = {
  intro: string;
  questions: QuestionnaireQuestion[];
};

/* --------------------------- Combined ----------------------------- */

export type WorkshopModalContent = {
  agenda: AgendaContent;
  map: MapContent;
  bring: BringContent;
  questionnaire: QuestionnaireContent;
};

/* ----------------------- WS4 — full content ----------------------- */

const WS4_AGENDA: AgendaContent = {
  date: "Thursday 22 May 2026",
  venue: "1 Canada Square, Level 18",
  timeRange: "09:00 – 17:00",
  sessions: [
    {
      time: "09:00",
      title: "Welcome & Context Setting",
      detail: "30 min · All attendees",
      durationMin: 30,
      type: "session",
    },
    {
      time: "09:30",
      title: "Human-Agent Taxonomy Deep Dive",
      detail: "60 min · Facilitated workshop",
      durationMin: 60,
      type: "session",
    },
    {
      time: "10:30",
      title: "Morning Break",
      detail: "15 min · Catered",
      durationMin: 15,
      type: "break",
    },
    {
      time: "10:45",
      title: "Process Mapping Sprint",
      detail: "75 min · Team breakouts",
      durationMin: 75,
      type: "session",
    },
    {
      time: "12:00",
      title: "Lunch Break",
      detail: "60 min · Catered",
      durationMin: 60,
      type: "break",
    },
    {
      time: "13:00",
      title: "Ops Model Co-Design",
      detail: "90 min · Collaborative design",
      durationMin: 90,
      type: "session",
    },
    {
      time: "14:30",
      title: "Afternoon Break",
      detail: "15 min · Catered",
      durationMin: 15,
      type: "break",
    },
    {
      time: "14:45",
      title: "Ways of Working Synthesis",
      detail: "60 min · Full group",
      durationMin: 60,
      type: "session",
    },
    {
      time: "16:00",
      title: "Readout & Next Steps",
      detail: "60 min · Presentations + close",
      durationMin: 60,
      type: "session",
    },
  ],
};

const WS4_MAP: MapContent = {
  venueName: "Level 18 Conference Suite, 1 Canada Square",
  address: "1 Canada Square, Canary Wharf, London E14 5AB",
  date: "Thursday 22 May 2026",
  time: "09:00 – 17:00",
  parking: "Multi-storey on Churchill Place — P5 level. Quote XAES WS4 at the gate.",
  refreshments: "Morning tea, lunch, and afternoon tea provided. Vegetarian and GF options on request.",
};

const WS4_BRING: BringContent = {
  categories: [
    {
      label: "Essentials",
      tag: "REQUIRED",
      items: [
        {
          label: "Photo ID",
          description: "Required for venue check-in at Level 1 reception.",
        },
        {
          label: "Laptop or Tablet",
          description: "Charged — power boards available throughout the venue.",
        },
        {
          label: "XAES Access Credentials",
          description: "Login details sent via email — check spam if not received.",
        },
      ],
    },
    {
      label: "Recommended",
      tag: "OPTIONAL",
      items: [
        {
          label: "Notebook & Pen",
          description: "For analogue sketching during design sprints.",
        },
        {
          label: "Process Documentation",
          description: "Existing flowcharts or SOPs relevant to your use case.",
        },
        {
          label: "Questions & Pain Points",
          description: "Prepared list of current workflow challenges for discussion.",
        },
      ],
    },
  ],
  note: "Smart casual dress. The venue is air conditioned — a light jacket is recommended.",
};

const WS4_QUESTIONNAIRE: QuestionnaireContent = {
  intro:
    "Please complete the following questions before the workshop. Your responses help the facilitation team tailor the session to your team's context.",
  questions: [
    {
      id: "q1",
      label: "What is your primary role in relation to AI adoption?",
      type: "choice",
      options: [
        "Decision maker",
        "Process lead",
        "Engineering",
        "Frontline user",
        "Other",
      ],
    },
    {
      id: "q2",
      label: "How would you rate your team's current AI readiness?",
      type: "scale",
      min: 1,
      max: 5,
      placeholder: "1 (not ready) – 5 (leading)",
    },
    {
      id: "q3",
      label: "What is your biggest concern about human-agent collaboration?",
      type: "textarea",
      placeholder: "Accountability, change management, data quality, trust...",
    },
    {
      id: "q4",
      label: "What outcome matters most to you from today's workshop?",
      type: "textarea",
      placeholder: "Type your response here...",
    },
  ],
};

/* ----------------------- Generic placeholder ---------------------- */

function placeholderContent(workshopId: WorkshopId): WorkshopModalContent {
  return {
    agenda: {
      date: "Date to be confirmed",
      venue: "Venue TBC",
      timeRange: "09:00 – 17:00",
      sessions: [
        {
          time: "09:00",
          title: "Workshop kickoff",
          detail: "30 min · All attendees",
          durationMin: 30,
          type: "session",
        },
        {
          time: "09:30",
          title: "Working session",
          detail: "120 min · Facilitated",
          durationMin: 120,
          type: "session",
        },
        {
          time: "11:30",
          title: "Break",
          detail: "15 min · Catered",
          durationMin: 15,
          type: "break",
        },
        {
          time: "11:45",
          title: "Readout & Next Steps",
          detail: "75 min · Full group",
          durationMin: 75,
          type: "session",
        },
      ],
    },
    map: {
      venueName: "Venue TBC",
      address: "Address to be confirmed",
      date: "Date to be confirmed",
      time: "09:00 – 17:00",
      parking: "Parking details to be confirmed.",
      refreshments: "Refreshments to be confirmed.",
    },
    bring: {
      categories: [
        {
          label: "Essentials",
          tag: "REQUIRED",
          items: [
            {
              label: "Photo ID",
              description: "Required for venue check-in.",
            },
            {
              label: "Laptop or Tablet",
              description: "Charged — power available at the venue.",
            },
          ],
        },
        {
          label: "Recommended",
          tag: "OPTIONAL",
          items: [
            {
              label: "Notebook & Pen",
              description: "For analogue notes during the session.",
            },
          ],
        },
      ],
      note: `Generic checklist for ${workshopId}. WS4 has the canonical version — other workshops inherit this placeholder until their content is authored.`,
    },
    questionnaire: {
      intro:
        "Please complete the following questions before the workshop. Your responses help the facilitation team tailor the session.",
      questions: [
        {
          id: "q1",
          label: "What is your primary role in this workshop?",
          type: "input",
          placeholder: "e.g. Sponsor, lead, contributor",
        },
        {
          id: "q2",
          label: "What is the single most important outcome for you?",
          type: "textarea",
          placeholder: "Type your response here...",
        },
        {
          id: "q3",
          label: "What concerns or questions would you like addressed?",
          type: "textarea",
          placeholder: "Type your response here...",
        },
      ],
    },
  };
}

/* ----------------------------- Registry --------------------------- */

export const modalContentByWorkshop: Record<WorkshopId, WorkshopModalContent> = {
  WS1: placeholderContent("WS1"),
  WS2: placeholderContent("WS2"),
  WS3: placeholderContent("WS3"),
  WS4: {
    agenda: WS4_AGENDA,
    map: WS4_MAP,
    bring: WS4_BRING,
    questionnaire: WS4_QUESTIONNAIRE,
  },
  WS5: placeholderContent("WS5"),
  WS6: placeholderContent("WS6"),
  WS7: placeholderContent("WS7"),
  WS8: placeholderContent("WS8"),
};
